import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { merge } from 'lodash';

import { CarrierContractsApiInstance } from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';

import { CarrierContractDetail } from '../queries';

export interface OneOf {
  const: number;
  title: string;
}

export type Schema =
  | { type: 'string'; value?: string }
  | { type: 'number'; value?: number }
  | { type: 'boolean'; value?: boolean }
  | { type: 'object'; properties: Record<string, Schema> }
  | { type: 'array'; items: Schema; value?: string[] }
  | { type: 'integer'; oneOf?: OneOf[]; value?: number };

export interface UISchema {
  widget?: string;
  description?: string;
  optional?: boolean;
}

export type SchemaValues = Record<
  string,
  {
    value?: string | number | string[];
    properties?: SchemaValues;
  }
>;

export function useCarrierContractSchemaQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['carrierContractSchemas', siteId],
    lists: () => [...keys.all, 'list'] as const,
    details: () => [...keys.all, 'detail'] as const,
    detail: (id: string) => [...keys.details(), id] as const,
  };

  return keys;
}

export function useCarrierContractsListSchemasQuery() {
  const queryKey = useCarrierContractSchemaQueryKey();

  const { data, ...query } = useSuspenseQuery({
    queryFn: async () => {
      const { data } = await CarrierContractsApiInstance.schemas.carrierContractsListSchemas({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
        ingrid_products: 2 as any, // future (create contracts for different products)
      });

      return data;
    },
    queryKey: queryKey.lists(),
  });

  return {
    data: data.contract_schemas ?? [],
    ...query,
  };
}

export function useCarrierContractSchemaQuery(schemaId: string) {
  const queryKey = useCarrierContractSchemaQueryKey();

  return useQuery({
    queryFn: async () => {
      const { data } = await CarrierContractsApiInstance.schemas.carrierContractsGetContractSchema(schemaId);

      return {
        json_schema: JSON.parse(data.json_schema!) as Record<string, Schema>,
        ui_schema: JSON.parse(data.ui_schema!) as Record<string, UISchema>,
      };
    },
    queryKey: queryKey.detail(schemaId),
    enabled: !!schemaId,
  });
}

export function useCarrierContractCombinedSchemaQuery(
  contract?: Pick<CarrierContractDetail, 'carrier_configuration_data' | 'schema_id'>
) {
  const { data, ...query } = useCarrierContractSchemaQuery(contract?.schema_id ?? '');

  return {
    data: {
      json_schema: merge({}, data?.json_schema ?? {}, valueify(contract?.carrier_configuration_data ?? {})),
      ui_schema: data?.ui_schema ?? {},
    },
    ...query,
  };
}

/**
 * In order to merge values into the combined schema we first have
 * to transform the values into a similar structure, which is done via this function.
 *
 * Input:
 * { "api_key": "abc-123" }
 *
 * Output:
 * { "api_key": { "value": "abc-123" } }
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function valueify(obj: Record<string, any>): SchemaValues {
  const transformed: SchemaValues = {};

  for (const key in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) {
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        transformed[key] = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          properties: valueify(obj[key]),
        };
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        transformed[key] = { value: obj[key] };
      }
    }
  }

  return transformed;
}
